import * as React from 'react';

/**
 * Gap value type - maps to spacing scale
 * 0 = 0, 1 = 4px, 2 = 8px, 3 = 12px, 4 = 16px, 5 = 20px, 6 = 24px, 8 = 32px, 10 = 40px, 12 = 48px, 16 = 64px
 */
export type StackGap = 0 | 1 | 2 | 3 | 4 | 5 | 6 | 8 | 10 | 12 | 16;

/**
 * Stack direction
 */
export type StackDirection = 'row' | 'column' | 'row-reverse' | 'column-reverse';

/**
 * Alignment options (align-items)
 */
export type StackAlign = 'start' | 'center' | 'end' | 'stretch' | 'baseline';

/**
 * Justification options (justify-content)
 */
export type StackJustify = 'start' | 'center' | 'end' | 'between' | 'around' | 'evenly';

/**
 * Wrap options
 */
export type StackWrap = boolean | 'wrap' | 'nowrap' | 'wrap-reverse';

export interface StackProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * Direction of the stack layout
   * @default 'column'
   */
  direction?: StackDirection;
  /**
   * Gap between items (maps to spacing scale)
   * 0 = 0, 1 = 4px, 2 = 8px, 3 = 12px, 4 = 16px, 5 = 20px, 6 = 24px, 8 = 32px, 10 = 40px, 12 = 48px, 16 = 64px
   * @default 4
   */
  gap?: StackGap;
  /**
   * Alignment of items on the cross axis
   * @default 'stretch'
   */
  align?: StackAlign;
  /**
   * Justification of items on the main axis
   * @default 'start'
   */
  justify?: StackJustify;
  /**
   * Whether items should wrap
   * @default false
   */
  wrap?: StackWrap;
  /**
   * Show dividers between items
   * Can be true (renders a default line) or a custom ReactNode
   */
  divider?: true | React.ReactElement;
  /**
   * Use inline-flex instead of flex
   * @default false
   */
  inline?: boolean;
  /**
   * Render as different element type
   * @default 'div'
   */
  as?: React.ElementType;
  /**
   * Stack children
   */
  children?: React.ReactNode;
}

/**
 * Gap classes mapping to Tailwind gap utilities
 */
const gapClasses: Record<StackGap, string> = {
  0: 'gap-0',
  1: 'gap-1',
  2: 'gap-2',
  3: 'gap-3',
  4: 'gap-4',
  5: 'gap-5',
  6: 'gap-6',
  8: 'gap-8',
  10: 'gap-10',
  12: 'gap-12',
  16: 'gap-16',
};

/**
 * Direction classes mapping
 */
const directionClasses: Record<StackDirection, string> = {
  row: 'flex-row',
  column: 'flex-col',
  'row-reverse': 'flex-row-reverse',
  'column-reverse': 'flex-col-reverse',
};

/**
 * Alignment classes mapping
 */
const alignClasses: Record<StackAlign, string> = {
  start: 'items-start',
  center: 'items-center',
  end: 'items-end',
  stretch: 'items-stretch',
  baseline: 'items-baseline',
};

/**
 * Justification classes mapping
 */
const justifyClasses: Record<StackJustify, string> = {
  start: 'justify-start',
  center: 'justify-center',
  end: 'justify-end',
  between: 'justify-between',
  around: 'justify-around',
  evenly: 'justify-evenly',
};

/**
 * Get wrap class from wrap prop
 */
function getWrapClass(wrap: StackWrap): string {
  if (wrap === true || wrap === 'wrap') {
    return 'flex-wrap';
  }
  if (wrap === 'wrap-reverse') {
    return 'flex-wrap-reverse';
  }
  return 'flex-nowrap';
}

/**
 * Default divider component for horizontal layout (vertical line between items)
 */
function HorizontalDivider() {
  return (
    <hr
      className="w-px self-stretch border-0 border-l border-secondary-200"
      aria-orientation="vertical"
    />
  );
}

/**
 * Default divider component for vertical layout (horizontal line between items)
 */
function VerticalDivider() {
  return (
    <hr
      className="h-px w-full border-0 border-t border-secondary-200"
      aria-orientation="horizontal"
    />
  );
}

/**
 * Interleave children with dividers
 */
function interleaveWithDividers(
  children: React.ReactNode,
  divider: React.ReactNode,
  direction: StackDirection
): React.ReactNode[] {
  const childArray = React.Children.toArray(children).filter(Boolean);
  const isHorizontal = direction === 'row' || direction === 'row-reverse';

  // Determine the actual divider to use
  let actualDivider: React.ReactNode;
  if (divider === true) {
    actualDivider = isHorizontal ? <HorizontalDivider /> : <VerticalDivider />;
  } else {
    actualDivider = divider;
  }

  const result: React.ReactNode[] = [];
  childArray.forEach((child, index) => {
    result.push(child);
    if (index < childArray.length - 1) {
      result.push(
        <React.Fragment key={`divider-${String(index)}`}>
          {actualDivider}
        </React.Fragment>
      );
    }
  });

  return result;
}

/**
 * Stack component
 *
 * A flexible layout component for arranging children in a row or column
 * with consistent spacing. Uses CSS flexbox under the hood.
 *
 * @example
 * // Basic vertical stack
 * <Stack gap={4}>
 *   <div>Item 1</div>
 *   <div>Item 2</div>
 *   <div>Item 3</div>
 * </Stack>
 *
 * @example
 * // Horizontal stack with alignment
 * <Stack direction="row" gap={4} align="center">
 *   <Button>Action 1</Button>
 *   <Button>Action 2</Button>
 * </Stack>
 *
 * @example
 * // Stack with dividers
 * <Stack gap={4} divider>
 *   <div>Section 1</div>
 *   <div>Section 2</div>
 *   <div>Section 3</div>
 * </Stack>
 *
 * @example
 * // Render as different element
 * <Stack as="nav" direction="row" gap={4}>
 *   <a href="/">Home</a>
 *   <a href="/about">About</a>
 * </Stack>
 */
const Stack = React.forwardRef<HTMLDivElement, StackProps>(
  (
    {
      direction = 'column',
      gap = 4,
      align = 'stretch',
      justify = 'start',
      wrap = false,
      divider,
      inline = false,
      as: Component = 'div',
      className = '',
      children,
      ...props
    },
    ref
  ) => {
    const combinedClasses = [
      inline ? 'inline-flex' : 'flex',
      directionClasses[direction],
      // Only apply gap if no dividers (dividers have their own spacing)
      !divider && gapClasses[gap],
      alignClasses[align],
      justifyClasses[justify],
      getWrapClass(wrap),
      className,
    ]
      .filter(Boolean)
      .join(' ');

    // Process children with dividers if needed
    const processedChildren = divider
      ? interleaveWithDividers(children, divider, direction)
      : children;

    return (
      <Component ref={ref} className={combinedClasses} {...props}>
        {processedChildren}
      </Component>
    );
  }
);

Stack.displayName = 'Stack';

/**
 * HStack component
 *
 * A convenience wrapper for Stack with direction="row".
 * Creates a horizontal layout with flexbox.
 *
 * @example
 * <HStack gap={4} align="center">
 *   <Button>Cancel</Button>
 *   <Button variant="primary">Submit</Button>
 * </HStack>
 */
const HStack = React.forwardRef<HTMLDivElement, Omit<StackProps, 'direction'>>(
  (props, ref) => {
    return <Stack ref={ref} direction="row" {...props} />;
  }
);

HStack.displayName = 'HStack';

/**
 * VStack component
 *
 * A convenience wrapper for Stack with direction="column".
 * Creates a vertical layout with flexbox.
 *
 * @example
 * <VStack gap={4}>
 *   <TextField label="Email" />
 *   <TextField label="Password" type="password" />
 *   <Button variant="primary">Sign In</Button>
 * </VStack>
 */
const VStack = React.forwardRef<HTMLDivElement, Omit<StackProps, 'direction'>>(
  (props, ref) => {
    return <Stack ref={ref} direction="column" {...props} />;
  }
);

VStack.displayName = 'VStack';

export { Stack, HStack, VStack };
